import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  BadgePercent,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  Clock3,
  Gift,
  RefreshCw,
  Search,
  Sparkles,
  Store,
  Tag,
  TicketPercent,
  Trash2,
  Users,
  WandSparkles,
  XCircle,
  Zap,
} from "lucide-react";
import { couponsService } from "../../../services/coupons.service";
import CouponConditionFields, { buildCouponConditionPayload, validateCouponCondition } from "../../../components/Coupons/CouponConditionFields";
import { warehouseCouponsService } from "../../../services/warehouseCoupons.service";
import { warehouseService } from "../../../services/warehouse.service";
import { getCurrentDashboard, getAuthSession } from "../../AdminAuth/components/authStore";
import dashboardGroceryBag from "../../../assets/dashboard-grocery-bag.png";
import dashboardHeaderProduce from "../../../assets/dashboard-header-produce.png";

const emptyForm = {
  code: "",
  title: "",
  description: "",
  discountType: "PERCENT",
  discountValue: "",
  maxDiscountAmount: "",
  minOrderAmount: "",
  startsAt: "",
  endsAt: "",
  usageLimit: "",
  perUserLimit: "1",
  warehouseId: "",
  conditionType: "NONE",
  firstOrderOnly: false,
  validDays: [],
  validFromTime: "",
  validToTime: "",
  allowedCities: "",
  allowedStates: "",
  allowedPincodes: "",
};

function StyledSelect({
  value,
  onChange,
  options = [],
  placeholder = "Select",
  disabled = false,
  compact = false,
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const selected =
    options.find((option) => String(option.value) === String(value)) ||
    options[0] ||
    { value: "", label: placeholder };

  useEffect(() => {
    const handleOutside = (event) => {
      if (!wrapRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((prev) => !prev)}
        className={`flex w-full items-center justify-between border bg-white text-left text-sm outline-none transition ${compact
            ? "h-[40px] rounded-[12px] px-3"
            : "h-[48px] rounded-[16px] px-4"
          } ${disabled
            ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400"
            : open
              ? "border-[#d86a78] ring-2 ring-[#f9dfe4]"
              : "border-slate-200 text-slate-700 hover:border-[#d86a78]"
          }`}
      >
        <span className="min-w-0 flex-1 truncate">
          {disabled ? "Loading..." : selected?.label || placeholder}
        </span>
        <ChevronDown
          size={16}
          className={`ml-3 shrink-0 text-slate-400 transition ${open ? "rotate-180 text-[#d86a78]" : ""
            }`}
        />
      </button>

      {open && !disabled ? (
        <div
          className={`absolute left-0 right-0 z-[120] max-h-[260px] overflow-y-auto border border-[#f2c6cc] bg-white p-1 shadow-[0_18px_45px_rgba(15,23,42,0.16)] ${compact ? "top-[46px] rounded-[14px]" : "top-[54px] rounded-[16px]"
            }`}
        >
          {options.map((option) => {
            const active = String(option.value) === String(value);

            return (
              <button
                key={String(option.value || "all")}
                type="button"
                disabled={option.disabled}
                onClick={() => {
                  if (option.disabled) return;
                  onChange?.(option.value);
                  setOpen(false);
                }}
                className={`flex w-full items-center justify-between rounded-[12px] px-3 py-2.5 text-left text-sm transition ${option.disabled
                    ? "cursor-not-allowed text-slate-300"
                    : active
                      ? "bg-[#fff1f3] font-semibold text-[#d86a78]"
                      : "text-slate-700 hover:bg-[#fff7f8] hover:text-[#d86a78]"
                  }`}
              >
                <span className="truncate">{option.label}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function toArray(value) {
  return Array.isArray(value) ? value : [];
}

function normalizeList(value) {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.data?.items)) return value.data.items;
  if (Array.isArray(value?.data?.rows)) return value.data.rows;
  if (Array.isArray(value?.rows)) return value.rows;
  if (Array.isArray(value?.warehouses)) return value.warehouses;
  return [];
}

function normalizeWarehouse(item = {}) {
  const id = String(item?.id || item?._id || item?.warehouseId || item?.warehouse_id || "").trim();
  const name = String(
    item?.name ||
    item?.warehouseName ||
    item?.warehouse_name ||
    item?.title ||
    item?.code ||
    id
  ).trim();

  return {
    id,
    name: name || id || "Warehouse",
    city: item?.city || item?.address?.city || item?.location?.city || "",
    state: item?.state || item?.address?.state || item?.location?.state || "",
  };
}

function money(value) {
  return `₹${Number(value || 0).toFixed(2)}`;
}

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

function normalizeCoupon(item = {}) {
  return {
    id: item.id,
    code: item.code || "",
    title: item.title || "",
    discountType: item.discountType || item.discount_type || "PERCENT",
    discountValue: Number(item.discountValue ?? item.discount_value ?? 0),
    maxDiscountAmount: item.maxDiscountAmount ?? item.max_discount_amount ?? null,
    minOrderAmount: Number(item.minOrderAmount ?? item.min_order_amount ?? 0),
    usageLimit: item.usageLimit ?? item.usage_limit ?? null,
    perUserLimit: Number(item.perUserLimit ?? item.per_user_limit ?? 1),
    warehouseId: item.warehouseId || item.warehouse_id || null,
    createdByType: item.createdByType || item.created_by_type || "ADMIN",
    approvalStatus: item.approvalStatus || item.approval_status || "PENDING",
    rejectionReason: item.rejectionReason || item.rejection_reason || "",
    isActive: item.isActive ?? item.is_active ?? false,
    usedCount: Number(item.usedCount ?? item.used_count ?? 0),
    startsAt: item.startsAt || item.starts_at || null,
    endsAt: item.endsAt || item.ends_at || null,
    createdAt: item.createdAt || item.created_at || null,
    metadata: item.metadata || {},
    specialRules: item.specialRules || item.special_rules || item.metadata?.rules || {},
    readOnly: Boolean(item.readOnly || item.read_only),
  };
}

function statusClass(status) {
  const v = String(status || "").toUpperCase();
  if (v === "APPROVED") return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (v === "REJECTED") return "bg-rose-50 text-rose-700 border-rose-200";
  return "bg-amber-50 text-amber-700 border-amber-200";
}

function activeClass(isActive) {
  return isActive
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : "bg-slate-50 text-slate-600 border-slate-200";
}

function usageStatusClass(status) {
  const v = String(status || '').toUpperCase();
  if (v === 'REDEEMED') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (v === 'RESERVED') return 'bg-amber-50 text-amber-700 border-amber-200';
  return 'bg-slate-50 text-slate-600 border-slate-200';
}

function FormLabel({ children, optional = false }) {
  return (
    <div className="mb-1.5 flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.16em] text-slate-500">
      <span>{children}</span>
      {optional ? <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[9px] tracking-normal text-slate-400">Optional</span> : null}
    </div>
  );
}

function StatTile({ icon: Icon, label, value, hint, tone = "rose" }) {
  const tones = {
    rose: { card: "border-rose-100 from-rose-50 to-pink-50", icon: "bg-rose-100/80 text-rose-600" },
    emerald: { card: "border-emerald-100 from-emerald-50 to-teal-50", icon: "bg-emerald-100/80 text-emerald-600" },
    amber: { card: "border-amber-100 from-amber-50 to-orange-50", icon: "bg-amber-100/80 text-amber-600" },
    violet: { card: "border-violet-100 from-violet-50 to-fuchsia-50", icon: "bg-violet-100/80 text-violet-600" },
  };
  const selected = tones[tone] || tones.rose;

  return (
    <div className={`rounded-[22px] border bg-gradient-to-br p-4 shadow-sm ${selected.card}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-400">{label}</div>
          <div className="mt-2 text-[24px] font-black leading-none text-slate-950">{value}</div>
          {hint ? <div className="mt-2 text-[11px] font-medium text-slate-500">{hint}</div> : null}
        </div>
        <div className={`flex h-10 w-10 items-center justify-center rounded-[14px] ${selected.icon}`}>
          <Icon size={18} />
        </div>
      </div>
    </div>
  );
}

function normalizeCouponUsage(item = {}) {
  const order = item.order || {};
  const user = item.user || {};
  return {
    id: item.id,
    userId: item.userId || user.id || '',
    userName: user.name || 'Unknown user',
    userPhone: user.phone || '',
    userEmail: user.email || '',
    orderId: item.orderId || order.id || '',
    orderNumber: item.orderNumber || order.number || '',
    orderStatus: order.status || '',
    paymentStatus: order.paymentStatus || '',
    subtotal: Number(order.subtotal || 0),
    deliveryFee: Number(order.deliveryFee || 0),
    taxAmount: Number(order.taxAmount || 0),
    couponDiscountAmount: Number(item.couponDiscountAmount || order.discountAmount || 0),
    grandTotal: Number(order.grandTotal || 0),
    itemCount: Number(order.itemCount || 0),
    totalQty: Number(order.totalQty || 0),
    addressText: order.addressText || '',
    redemptionStatus: item.redemptionStatus || '',
    appliedAt: item.appliedAt || item.createdAt || '',
  };
}

function warehouseLabel(warehouseId, warehouseMap) {
  if (!warehouseId) return "All warehouses";
  const warehouse = warehouseMap.get(String(warehouseId));
  if (!warehouse) return "Selected warehouse";
  const place = [warehouse.city, warehouse.state].filter(Boolean).join(", ");
  return place ? `${warehouse.name} — ${place}` : warehouse.name;
}

const DAY_OPTIONS = [
  { value: 0, label: "Sun" },
  { value: 1, label: "Mon" },
  { value: 2, label: "Tue" },
  { value: 3, label: "Wed" },
  { value: 4, label: "Thu" },
  { value: 5, label: "Fri" },
  { value: 6, label: "Sat" },
];

function rulesOf(item = {}) {
  const rules = item.specialRules || item.metadata?.rules || item.rules || {};
  return {
    firstOrderOnly: Boolean(rules.firstOrderOnly || rules.first_order_only),
    validDays: Array.isArray(rules.validDays || rules.valid_days) ? (rules.validDays || rules.valid_days).map(Number) : [],
    validFromTime: rules.validFromTime || rules.valid_from_time || "",
    validToTime: rules.validToTime || rules.valid_to_time || "",
    cities: rules.cities || rules.allowedCities || rules.allowed_cities || [],
    states: rules.states || rules.allowedStates || rules.allowed_states || [],
    pincodes: rules.pincodes || rules.allowedPincodes || rules.allowed_pincodes || [],
  };
}

function formatRules(item = {}) {
  const rules = rulesOf(item);
  const parts = [];
  if (rules.firstOrderOnly) parts.push("First order only");
  if (rules.validDays.length) {
    const labels = rules.validDays.map((day) => DAY_OPTIONS.find((d) => d.value === Number(day))?.label).filter(Boolean);
    if (labels.length) parts.push(`Days: ${labels.join(", ")}`);
  }
  if (rules.validFromTime || rules.validToTime) parts.push(`Time: ${rules.validFromTime || "00:00"}-${rules.validToTime || "23:59"}`);
  if (rules.cities?.length) parts.push(`Cities: ${rules.cities.join(", ")}`);
  if (rules.states?.length) parts.push(`States: ${rules.states.join(", ")}`);
  if (rules.pincodes?.length) parts.push(`Pincodes: ${rules.pincodes.join(", ")}`);
  return parts.join(" · ") || "No special rules";
}

function compactPayload(form, isWarehouseMode) {
  const payload = {
    code: String(form.code || "").trim().toUpperCase(),
    title: String(form.title || "").trim(),
    description: String(form.description || "").trim() || null,
    discountType: form.discountType,
    discountValue: Number(form.discountValue || 0),
    maxDiscountAmount:
      form.discountType === "FIXED" || form.maxDiscountAmount === ""
        ? null
        : Number(form.maxDiscountAmount),
    minOrderAmount: Number(form.minOrderAmount || 0),
    startsAt: form.startsAt || null,
    endsAt: form.endsAt || null,
    usageLimit: form.usageLimit === "" ? null : Number(form.usageLimit),
    perUserLimit: Number(form.perUserLimit || 1),
    ...buildCouponConditionPayload(form),
  };

  if (!isWarehouseMode && form.warehouseId) payload.warehouseId = form.warehouseId;
  return payload;
}

export default function CouponsPage() {
  const isWarehouseMode = getCurrentDashboard() === "warehouse";
  const session = getAuthSession() || {};
  const selectedWarehouseId = session?.selectedWarehouseId || session?.warehouseAccess?.assignedWarehouseId || "";
  const api = isWarehouseMode ? warehouseCouponsService : couponsService;

  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ page: 1, limit: 20, total: 0 });
  const [filters, setFilters] = useState({ status: "", active: "", warehouseId: "", search: "" });
  const [form, setForm] = useState(() => ({ ...emptyForm, warehouseId: selectedWarehouseId || "" }));
  const [loading, setLoading] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseLoading, setWarehouseLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [usageCoupon, setUsageCoupon] = useState(null);
  const [usageItems, setUsageItems] = useState([]);
  const [usageMeta, setUsageMeta] = useState({ page: 1, limit: 10, total: 0 });
  const [usageSummary, setUsageSummary] = useState(null);
  const [usageStatus, setUsageStatus] = useState("");
  const [usageSearch, setUsageSearch] = useState("");
  const [usageLoading, setUsageLoading] = useState(false);

  const normalizedItems = useMemo(() => toArray(items).map(normalizeCoupon), [items]);
  const warehouseMap = useMemo(() => new Map(warehouses.map((w) => [String(w.id), w])), [warehouses]);

  async function loadCoupons(nextPage = meta.page || 1) {
    setLoading(true);
    setError("");
    try {
      if (isWarehouseMode) {
        const ownRes = await warehouseCouponsService.list({
          page: nextPage,
          limit: meta.limit || 20,
          status: filters.status,
          isActive: filters.active,
          search: filters.search,
        });
        let adminItems = [];
        try {
          const adminRes = await warehouseCouponsService.listAdminReadOnly({
            page: 1,
            limit: 100,
            status: "APPROVED",
            isActive: true,
            warehouseId: selectedWarehouseId || undefined,
            search: filters.search,
          });
          adminItems = toArray(adminRes?.items).map((item) => ({ ...item, readOnly: true }));
        } catch {
          adminItems = [];
        }
        const ownItems = toArray(ownRes?.items);
        const seen = new Set();
        const merged = [...adminItems, ...ownItems].filter((item) => {
          const key = String(item?.id || item?.code || Math.random());
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        setItems(merged);
        setMeta(ownRes?.meta || { page: nextPage, limit: 20, total: merged.length });
        return;
      }

      const res = await couponsService.list({
        page: nextPage,
        limit: meta.limit || 20,
        status: filters.status,
        isActive: filters.active,
        search: filters.search,
        warehouseId: filters.warehouseId,
      });
      setItems(toArray(res?.items));
      setMeta(res?.meta || { page: nextPage, limit: 20, total: toArray(res?.items).length });
    } catch (err) {
      setError(err?.message || "Failed to load coupons");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (isWarehouseMode) return;

    let cancelled = false;

    async function loadWarehouses() {
      setWarehouseLoading(true);
      try {
        const res = await warehouseService.listWarehouses({ page: 1, limit: 300 });
        const list = normalizeList(res).map(normalizeWarehouse).filter((w) => w.id);
        if (!cancelled) setWarehouses(list);
      } catch (err) {
        if (!cancelled) {
          setWarehouses([]);
          setError(err?.message || "Failed to load warehouses");
        }
      } finally {
        if (!cancelled) setWarehouseLoading(false);
      }
    }

    loadWarehouses();

    return () => {
      cancelled = true;
    };
  }, [isWarehouseMode]);

  useEffect(() => {
    loadCoupons(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWarehouseMode]);

  function updateForm(key, value) {
    setForm((prev) => {
      if (key === "discountType") {
        return {
          ...prev,
          discountType: value,
          maxDiscountAmount: value === "FIXED" ? "" : prev.maxDiscountAmount,
        };
      }

      if (key === "maxDiscountAmount" && prev.discountType === "FIXED") {
        return prev;
      }

      if (key === "validDays") {
        if (Array.isArray(value)) {
          return { ...prev, validDays: value.map(Number).filter((day) => Number.isInteger(day) && day >= 0 && day <= 6).sort((a, b) => a - b) };
        }
        const day = Number(value);
        const current = Array.isArray(prev.validDays) ? prev.validDays : [];
        const next = current.includes(day) ? current.filter((item) => item !== day) : [...current, day].sort((a, b) => a - b);
        return { ...prev, validDays: next };
      }

      return { ...prev, [key]: value };
    });
  }

  async function handleCreate(e) {
    e.preventDefault();
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const conditionError = validateCouponCondition(form);
      if (conditionError) {
        setError(conditionError);
        return;
      }
      const payload = compactPayload(form, isWarehouseMode);
      const res = await api.create(payload);
      setMessage(
        isWarehouseMode
          ? "Coupon created and sent to admin for approval."
          : res?.message || "Coupon created."
      );
      setForm({ ...emptyForm, warehouseId: selectedWarehouseId || "" });
      await loadCoupons(1);
    } catch (err) {
      setError(err?.message || "Failed to create coupon");
    } finally {
      setSaving(false);
    }
  }

  async function runAction(action, successText) {
    setError("");
    setMessage("");
    try {
      await action();
      setMessage(successText);
      await loadCoupons(meta.page || 1);
    } catch (err) {
      setError(err?.message || "Action failed");
    }
  }

  async function rejectCoupon(id) {
    const reason = window.prompt("Rejection reason?");
    if (!reason) return;
    await runAction(() => couponsService.reject(id, reason), "Coupon rejected.");
  }

  async function deleteCoupon(item) {
    const ok = window.confirm(`Delete coupon ${item.code}? It will be removed from coupon history/list.`);
    if (!ok) return;
    await runAction(() => couponsService.delete(item.id), "Coupon deleted.");
  }


  async function loadCouponUsage(coupon = usageCoupon, nextPage = 1, overrides = {}) {
    if (isWarehouseMode || !coupon?.id) return;
    setUsageLoading(true);
    setError("");
    try {
      const res = await couponsService.usages(coupon.id, {
        page: nextPage,
        limit: usageMeta.limit || 10,
        status: overrides.status ?? usageStatus,
        search: overrides.search ?? usageSearch,
      });
      setUsageItems(toArray(res?.items).map(normalizeCouponUsage));
      setUsageMeta(res?.meta || { page: nextPage, limit: 10, total: toArray(res?.items).length });
      setUsageSummary(res?.summary || null);
    } catch (err) {
      setUsageItems([]);
      setUsageSummary(null);
      setError(err?.message || "Failed to load coupon usage details");
    } finally {
      setUsageLoading(false);
    }
  }

  async function toggleUsage(item) {
    if (isWarehouseMode) return;
    if (usageCoupon?.id === item.id) {
      setUsageCoupon(null);
      setUsageItems([]);
      setUsageSummary(null);
      return;
    }
    setUsageCoupon(item);
    setUsageItems([]);
    setUsageSummary(null);
    setUsageStatus("");
    setUsageSearch("");
    await loadCouponUsage(item, 1, { status: "", search: "" });
  }

  async function applyUsageFilters() {
    await loadCouponUsage(usageCoupon, 1);
  }

  function renderUsagePanel(item) {
    if (isWarehouseMode || usageCoupon?.id !== item.id) return null;

    const totalPages = Math.max(1, Math.ceil(Number(usageMeta.total || 0) / Number(usageMeta.limit || 10)));

    return (
      <tr>
        <td colSpan="8" className="rounded-2xl border border-[#f2c6cc] bg-white p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-sm font-black text-slate-950">Usage details — {item.code}</div>
              <div className="text-xs text-slate-500">Users, orders, coupon discount and paid amount.</div>
            </div>
            <button type="button" onClick={() => setUsageCoupon(null)} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-600">
              Close
            </button>
          </div>

          <div className="mt-4 grid gap-3 md:grid-cols-4">
            <div className="rounded-2xl bg-[#fffafa] p-3">
              <div className="text-xs text-slate-500">Total records</div>
              <div className="text-lg font-black text-slate-950">{usageSummary?.totalUsages ?? usageMeta.total ?? 0}</div>
            </div>
            <div className="rounded-2xl bg-[#fffafa] p-3">
              <div className="text-xs text-slate-500">Redeemed</div>
              <div className="text-lg font-black text-slate-950">{usageSummary?.redeemedUsages ?? 0}</div>
            </div>
            <div className="rounded-2xl bg-[#fffafa] p-3">
              <div className="text-xs text-slate-500">Coupon discount</div>
              <div className="text-lg font-black text-slate-950">{money(usageSummary?.totalDiscountAmount || 0)}</div>
            </div>
            <div className="rounded-2xl bg-[#fffafa] p-3">
              <div className="text-xs text-slate-500">Order amount</div>
              <div className="text-lg font-black text-slate-950">{money(usageSummary?.totalGrandTotal || 0)}</div>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <input
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
              placeholder="Search user/order"
              value={usageSearch}
              onChange={(e) => setUsageSearch(e.target.value)}
            />
            <StyledSelect
              compact
              className="min-w-[190px]"
              value={usageStatus}
              onChange={setUsageStatus}
              options={[
                { value: "", label: "All usage statuses" },
                { value: "REDEEMED", label: "Redeemed" },
                { value: "RESERVED", label: "Reserved" },
                { value: "RELEASED", label: "Released" },
              ]}
            />
            <button type="button" className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-bold text-white" onClick={applyUsageFilters}>
              Apply
            </button>
          </div>

          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-400">
                  <th className="px-3 py-2">User</th>
                  <th className="px-3 py-2">Order</th>
                  <th className="px-3 py-2">Amount</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Used at</th>
                </tr>
              </thead>
              <tbody>
                {usageItems.map((usage) => (
                  <tr key={usage.id || `${usage.orderId}-${usage.userId}`} className="border-t border-slate-100">
                    <td className="px-3 py-3 align-top">
                      <div className="font-bold text-slate-950">{usage.userName}</div>
                      {usage.userPhone ? <div className="text-xs text-slate-500">{usage.userPhone}</div> : null}
                      {usage.userEmail ? <div className="text-xs text-slate-500">{usage.userEmail}</div> : null}
                    </td>
                    <td className="px-3 py-3 align-top">
                      <div className="font-bold text-slate-950">{usage.orderNumber || "—"}</div>
                      <div className="text-xs text-slate-500">Items {usage.itemCount} · Qty {usage.totalQty}</div>
                      {usage.addressText ? <div className="mt-1 max-w-[360px] text-xs text-slate-500">{usage.addressText}</div> : null}
                    </td>
                    <td className="px-3 py-3 align-top">
                      <div className="text-xs text-slate-500">Subtotal {money(usage.subtotal)}</div>
                      <div className="text-xs text-emerald-700">Coupon -{money(usage.couponDiscountAmount)}</div>
                      <div className="font-black text-slate-950">Paid {money(usage.grandTotal)}</div>
                    </td>
                    <td className="px-3 py-3 align-top">
                      <span className={`inline-flex rounded-full border px-2 py-1 text-xs font-bold ${usageStatusClass(usage.redemptionStatus)}`}>{usage.redemptionStatus || "—"}</span>
                      <div className="mt-2 text-xs text-slate-500">Order: {usage.orderStatus || "—"}</div>
                      <div className="text-xs text-slate-500">Payment: {usage.paymentStatus || "—"}</div>
                    </td>
                    <td className="px-3 py-3 align-top text-slate-600">{formatDate(usage.appliedAt)}</td>
                  </tr>
                ))}
                {!usageItems.length ? (
                  <tr>
                    <td colSpan="5" className="px-3 py-8 text-center text-slate-500">
                      {usageLoading ? "Loading usage details..." : "No usage details found."}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          {usageMeta.total > usageMeta.limit ? (
            <div className="mt-4 flex items-center justify-end gap-2 text-xs text-slate-500">
              <button type="button" disabled={usageLoading || usageMeta.page <= 1} onClick={() => loadCouponUsage(item, usageMeta.page - 1)} className="rounded-lg border border-slate-200 px-3 py-1.5 font-bold disabled:opacity-50">
                Prev
              </button>
              <span>Page {usageMeta.page} of {totalPages}</span>
              <button type="button" disabled={usageLoading || usageMeta.page >= totalPages} onClick={() => loadCouponUsage(item, usageMeta.page + 1)} className="rounded-lg border border-slate-200 px-3 py-1.5 font-bold disabled:opacity-50">
                Next
              </button>
            </div>
          ) : null}
        </td>
      </tr>
    );
  }

  const totalCoupons = Number(meta.total || normalizedItems.length || 0);
  const activeCoupons = normalizedItems.filter((item) => item.isActive).length;
  const pendingCoupons = normalizedItems.filter((item) => String(item.approvalStatus).toUpperCase() === "PENDING").length;
  const totalRedemptions = normalizedItems.reduce((sum, item) => sum + Number(item.usedCount || 0), 0);
  const previewCode = String(form.code || "SAVE20").toUpperCase();
  const previewTitle = form.title || "A little extra joy on every cart";
  const previewDiscount = form.discountType === "FIXED"
    ? `${money(form.discountValue || 100)} OFF`
    : `${Number(form.discountValue || 20)}% OFF`;
  const previewMinOrder = Number(form.minOrderAmount || 0);
  const previewWarehouse = isWarehouseMode
    ? "Selected warehouse"
    : warehouseLabel(form.warehouseId, warehouseMap);
  const inputClass = "h-[48px] w-full rounded-[15px] border border-slate-200 bg-white px-4 text-[13px] font-semibold text-slate-700 outline-none transition placeholder:text-slate-400 hover:border-rose-200 focus:border-[#d86a78] focus:ring-4 focus:ring-rose-50";

  return (
    <div className="space-y-6 pb-8">
      <section className="relative overflow-hidden rounded-[30px] border border-[#f2c6cc] bg-[linear-gradient(120deg,#fff7f8_0%,#fffdf5_48%,#f8f4ff_100%)] shadow-[0_22px_55px_rgba(216,106,120,0.10)]">
        <div className="pointer-events-none absolute -right-10 -top-12 h-52 w-52 rounded-full bg-rose-200/30 blur-3xl" />
        <div className="pointer-events-none absolute bottom-0 left-[42%] h-40 w-40 rounded-full bg-amber-200/30 blur-3xl" />
        <img
          src={dashboardHeaderProduce}
          alt=""
          className="pointer-events-none absolute bottom-0 right-[8%] hidden h-[120px] select-none object-contain opacity-90 lg:block"
        />
        <img
          src={dashboardGroceryBag}
          alt=""
          className="pointer-events-none absolute -bottom-3 right-[1%] hidden h-[112px] select-none object-contain drop-shadow-[0_18px_24px_rgba(216,106,120,0.20)] xl:block"
        />

        <div className="relative grid gap-6 p-6 lg:grid-cols-[1.25fr_0.75fr] lg:p-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-rose-100 bg-white/85 px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.18em] text-[#d86a78] shadow-sm">
              <Sparkles size={14} /> Offer Studio
            </div>
            <h1 className="mt-4 text-[30px] font-black tracking-[-0.03em] text-slate-950 md:text-[36px]">
              Make every coupon feel like a <span className="text-[#d86a78]">tiny celebration.</span>
            </h1>
            <p className="mt-3 max-w-2xl text-[13px] font-medium leading-6 text-slate-600">
              Create targeted offers, control limits, approve warehouse coupons and track redemptions from one visual workspace.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/85 px-3 py-1.5 text-[11px] font-bold text-slate-600 shadow-sm"><Zap size={13} className="text-amber-500" /> Instant admin activation</span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/85 px-3 py-1.5 text-[11px] font-bold text-slate-600 shadow-sm"><Store size={13} className="text-violet-500" /> Warehouse-specific offers</span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/85 px-3 py-1.5 text-[11px] font-bold text-slate-600 shadow-sm"><Users size={13} className="text-emerald-500" /> Redemption tracking</span>
            </div>
          </div>

          <div className="relative z-10 rounded-[24px] border border-white/80 bg-white/75 p-4 shadow-[0_18px_40px_rgba(15,23,42,0.08)] backdrop-blur-xl">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">Campaign pulse</div>
                <div className="mt-1 text-[16px] font-black text-slate-900">Coupons at a glance</div>
              </div>
              <button
                type="button"
                onClick={() => loadCoupons(meta.page || 1)}
                className="inline-flex h-10 w-10 items-center justify-center rounded-[14px] border border-rose-100 bg-white text-[#d86a78] shadow-sm transition hover:-translate-y-0.5 hover:bg-rose-50"
                title="Refresh coupons"
              >
                <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
              </button>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="rounded-[18px] bg-rose-50/80 p-3"><div className="text-[10px] font-black uppercase tracking-[0.16em] text-rose-400">Mode</div><div className="mt-2 text-[14px] font-black text-slate-900">{isWarehouseMode ? "Warehouse" : "Admin"}</div></div>
              <div className="rounded-[18px] bg-emerald-50/80 p-3"><div className="text-[10px] font-black uppercase tracking-[0.16em] text-emerald-500">Active</div><div className="mt-2 text-[14px] font-black text-slate-900">{activeCoupons} live</div></div>
              <div className="rounded-[18px] bg-amber-50/80 p-3"><div className="text-[10px] font-black uppercase tracking-[0.16em] text-amber-500">Pending</div><div className="mt-2 text-[14px] font-black text-slate-900">{pendingCoupons}</div></div>
              <div className="rounded-[18px] bg-violet-50/80 p-3"><div className="text-[10px] font-black uppercase tracking-[0.16em] text-violet-500">Used</div><div className="mt-2 text-[14px] font-black text-slate-900">{totalRedemptions}</div></div>
            </div>
          </div>
        </div>
      </section>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <StatTile icon={TicketPercent} label="Total coupons" value={totalCoupons} hint="Current result set / total" tone="rose" />
        <StatTile icon={CheckCircle2} label="Active offers" value={activeCoupons} hint="Ready for customers" tone="emerald" />
        <StatTile icon={Clock3} label="Pending approval" value={pendingCoupons} hint="Warehouse coupons waiting" tone="amber" />
        <StatTile icon={Gift} label="Redemptions" value={totalRedemptions} hint="Usage across visible coupons" tone="violet" />
      </div>

      {message ? <div className="rounded-[18px] border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-700">{message}</div> : null}
      {error ? <div className="rounded-[18px] border border-rose-100 bg-rose-50 px-4 py-3 text-sm font-bold text-rose-700">{error}</div> : null}

      <section className="overflow-visible rounded-[28px] border border-[#f2c6cc] bg-white shadow-[0_18px_45px_rgba(15,23,42,0.06)]">
        <div className="flex flex-col gap-3 border-b border-rose-100 bg-[linear-gradient(90deg,#fff9fa_0%,#ffffff_55%,#fffaf2_100%)] px-6 py-5 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.18em] text-[#d86a78]"><WandSparkles size={14} /> Coupon Lab</div>
            <h2 className="mt-1 text-[21px] font-black text-slate-950">Build a new offer</h2>
            <p className="mt-1 text-[12px] font-medium text-slate-500">Set the discount, audience, timing and limits. Preview updates live as you type.</p>
          </div>
          <div className="inline-flex items-center gap-2 rounded-full bg-slate-950 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.14em] text-white">
            <BadgePercent size={13} /> {isWarehouseMode ? "Approval required" : "Goes live after create"}
          </div>
        </div>

        <div className="grid items-start gap-6 p-6 xl:grid-cols-[minmax(0,1fr)_340px]">
          <form onSubmit={handleCreate} className="min-w-0">
            <div className="rounded-[22px] border border-slate-100 bg-slate-50/50 p-5">
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-[14px] bg-rose-100 text-[#d86a78]"><Tag size={17} /></div>
                <div><div className="text-[14px] font-black text-slate-900">Offer basics</div><div className="text-[11px] font-medium text-slate-500">Give your coupon a memorable identity.</div></div>
              </div>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                <div><FormLabel>Coupon code</FormLabel><input className={inputClass} placeholder="SAVE20" value={form.code} onChange={(e) => updateForm("code", e.target.value.toUpperCase())} required /></div>
                <div><FormLabel>Offer title</FormLabel><input className={inputClass} placeholder="Weekend Saver" value={form.title} onChange={(e) => updateForm("title", e.target.value)} required /></div>
                <div><FormLabel>Discount type</FormLabel><StyledSelect value={form.discountType} onChange={(value) => updateForm("discountType", value)} options={[{ value: "PERCENT", label: "Percent discount" }, { value: "FIXED", label: "Fixed amount" }]} /></div>
                <div><FormLabel>Discount value</FormLabel><input className={inputClass} type="number" min="0" step="0.01" placeholder={form.discountType === "PERCENT" ? "20" : "100"} value={form.discountValue} onChange={(e) => updateForm("discountValue", e.target.value)} required /></div>
                <div>
                  <FormLabel optional>Max discount amount</FormLabel>
                  <input className={`${inputClass} ${form.discountType === "FIXED" ? "cursor-not-allowed bg-slate-100 text-slate-400" : ""}`} type="number" min="0" step="0.01" placeholder={form.discountType === "FIXED" ? "Not needed for fixed amount" : "e.g. 250"} value={form.discountType === "FIXED" ? "" : form.maxDiscountAmount} onChange={(e) => updateForm("maxDiscountAmount", e.target.value)} disabled={form.discountType === "FIXED"} />
                </div>
                <div><FormLabel optional>Minimum order</FormLabel><input className={inputClass} type="number" min="0" step="0.01" placeholder="e.g. 499" value={form.minOrderAmount} onChange={(e) => updateForm("minOrderAmount", e.target.value)} /></div>
                <div className="md:col-span-2 lg:col-span-3"><FormLabel optional>Description</FormLabel><input className={inputClass} placeholder="Short customer-facing description..." value={form.description} onChange={(e) => updateForm("description", e.target.value)} /></div>
              </div>
            </div>

            <div className="mt-5 rounded-[22px] border border-slate-100 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-[14px] bg-amber-100 text-amber-600"><CalendarDays size={17} /></div>
                <div><div className="text-[14px] font-black text-slate-900">Validity & limits</div><div className="text-[11px] font-medium text-slate-500">Control timing, usage and warehouse scope.</div></div>
              </div>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                <div><FormLabel optional>Starts at</FormLabel><input className={inputClass} type="datetime-local" value={form.startsAt} onChange={(e) => updateForm("startsAt", e.target.value)} /></div>
                <div><FormLabel optional>Ends at</FormLabel><input className={inputClass} type="datetime-local" value={form.endsAt} onChange={(e) => updateForm("endsAt", e.target.value)} /></div>
                <div><FormLabel optional>Total usage limit</FormLabel><input className={inputClass} type="number" min="1" placeholder="e.g. 1000" value={form.usageLimit} onChange={(e) => updateForm("usageLimit", e.target.value)} /></div>
                <div><FormLabel>Per user limit</FormLabel><input className={inputClass} type="number" min="1" placeholder="1" value={form.perUserLimit} onChange={(e) => updateForm("perUserLimit", e.target.value)} /></div>
                {!isWarehouseMode ? <div className="md:col-span-2"><FormLabel optional>Warehouse scope</FormLabel><StyledSelect value={form.warehouseId} onChange={(value) => updateForm("warehouseId", value)} disabled={warehouseLoading} options={[{ value: "", label: "All warehouses" }, ...warehouses.map((warehouse) => ({ value: warehouse.id, label: warehouseLabel(warehouse.id, warehouseMap) }))]} /></div> : null}
              </div>
            </div>

            <div className="mt-5 rounded-[22px] border border-violet-100 bg-[linear-gradient(180deg,#ffffff_0%,#fbf8ff_100%)] p-5">
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-[14px] bg-violet-100 text-violet-600"><Users size={17} /></div>
                <div><div className="text-[14px] font-black text-slate-900">Smart targeting</div><div className="text-[11px] font-medium text-slate-500">Apply first-order, day/time or location-based conditions.</div></div>
              </div>
              <CouponConditionFields form={form} onChange={updateForm} dayOptions={DAY_OPTIONS} />
            </div>

            <div className="mt-5 flex flex-col gap-3 rounded-[20px] border border-rose-100 bg-rose-50/50 p-4 sm:flex-row sm:items-center sm:justify-between">
              <div><div className="text-[13px] font-black text-slate-900">Ready to launch?</div><div className="mt-0.5 text-[11px] font-medium text-slate-500">Review the live ticket preview before saving.</div></div>
              <button disabled={saving} className="inline-flex h-[46px] items-center justify-center gap-2 rounded-[14px] bg-[#d86a78] px-5 text-[13px] font-black text-white shadow-[0_12px_22px_rgba(216,106,120,0.25)] transition hover:-translate-y-0.5 hover:bg-[#c95b69] disabled:cursor-not-allowed disabled:opacity-60" type="submit">
                <Sparkles size={16} /> {saving ? "Saving..." : isWarehouseMode ? "Send for approval" : "Create approved coupon"}
              </button>
            </div>
          </form>

          <aside className="xl:sticky xl:top-[84px]">
            <div className="rounded-[26px] border border-rose-100 bg-[linear-gradient(145deg,#fff4f6_0%,#fff9ec_48%,#f6f1ff_100%)] p-5 shadow-[0_18px_40px_rgba(216,106,120,0.12)]">
              <div className="flex items-center justify-between gap-3">
                <div><div className="text-[10px] font-black uppercase tracking-[0.18em] text-[#d86a78]">Live coupon preview</div><div className="mt-1 text-[14px] font-black text-slate-900">Customer view</div></div>
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-[#d86a78] shadow-sm"><Gift size={17} /></div>
              </div>

              <div className="relative mt-5 overflow-hidden rounded-[24px] bg-[linear-gradient(135deg,#df6678_0%,#ef8f78_50%,#a778d7_115%)] p-5 text-white shadow-[0_20px_35px_rgba(216,106,120,0.28)]">
                <div className="absolute -left-4 top-1/2 h-8 w-8 -translate-y-1/2 rounded-full bg-[#fff6f7]" />
                <div className="absolute -right-4 top-1/2 h-8 w-8 -translate-y-1/2 rounded-full bg-[#fff6f7]" />
                <div className="absolute right-4 top-3 text-white/15"><TicketPercent size={74} strokeWidth={1.5} /></div>
                <div className="relative">
                  <div className="flex items-start justify-between gap-3">
                    <div className="rounded-full bg-white/20 px-3 py-1 text-[10px] font-black uppercase tracking-[0.16em] backdrop-blur">{previewCode}</div>
                    <Sparkles size={17} className="animate-pulse" />
                  </div>
                  <div className="mt-7 text-[34px] font-black tracking-[-0.04em]">{previewDiscount}</div>
                  <div className="mt-2 max-w-[230px] text-[14px] font-black leading-5">{previewTitle}</div>
                  <div className="mt-5 border-t border-dashed border-white/35 pt-4 text-[11px] font-semibold text-white/85">
                    {previewMinOrder > 0 ? `On orders above ${money(previewMinOrder)}` : "No minimum order requirement"}
                  </div>
                </div>
              </div>

              <div className="mt-4 space-y-2 rounded-[18px] border border-white bg-white/80 p-4">
                <div className="flex items-center justify-between gap-3 text-[11px]"><span className="font-bold text-slate-400">Scope</span><span className="max-w-[190px] truncate font-black text-slate-700">{previewWarehouse}</span></div>
                <div className="flex items-center justify-between gap-3 text-[11px]"><span className="font-bold text-slate-400">Per user</span><span className="font-black text-slate-700">{form.perUserLimit || 1} use</span></div>
                <div className="flex items-center justify-between gap-3 text-[11px]"><span className="font-bold text-slate-400">Usage cap</span><span className="font-black text-slate-700">{form.usageLimit || "Unlimited"}</span></div>
                <div className="flex items-center justify-between gap-3 text-[11px]"><span className="font-bold text-slate-400">Status after create</span><span className={`rounded-full px-2 py-1 font-black ${isWarehouseMode ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"}`}>{isWarehouseMode ? "Pending" : "Approved"}</span></div>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <section className="rounded-[28px] border border-[#f2c6cc] bg-white p-5 shadow-[0_18px_45px_rgba(15,23,42,0.05)] md:p-6">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.18em] text-[#d86a78]"><Tag size={14} /> Coupon library</div>
            <h2 className="mt-1 text-[21px] font-black text-slate-950">Manage live & historical offers</h2>
            <p className="mt-1 text-[12px] font-medium text-slate-500">Search, approve, activate and inspect redemption details.</p>
          </div>

          <div className="flex flex-wrap gap-2 rounded-[18px] border border-slate-100 bg-slate-50/70 p-2">
            <div className="relative min-w-[190px] flex-1 sm:flex-none">
              <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input className="h-[40px] w-full rounded-[12px] border border-slate-200 bg-white pl-9 pr-3 text-[12px] font-semibold outline-none focus:border-[#d86a78]" placeholder="Search code or title" value={filters.search} onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))} />
            </div>
            {!isWarehouseMode ? <StyledSelect compact className="min-w-[185px]" value={filters.warehouseId} onChange={(value) => setFilters((p) => ({ ...p, warehouseId: value }))} disabled={warehouseLoading} options={[{ value: "", label: "All warehouses" }, ...warehouses.map((warehouse) => ({ value: warehouse.id, label: warehouseLabel(warehouse.id, warehouseMap) }))]} /> : null}
            <StyledSelect compact className="min-w-[175px]" value={filters.status} onChange={(value) => setFilters((p) => ({ ...p, status: value }))} options={[{ value: "", label: "All approval statuses" }, { value: "PENDING", label: "Pending" }, { value: "APPROVED", label: "Approved" }, { value: "REJECTED", label: "Rejected" }]} />
            <StyledSelect compact className="min-w-[155px]" value={filters.active} onChange={(value) => setFilters((p) => ({ ...p, active: value }))} options={[{ value: "", label: "Active + inactive" }, { value: "true", label: "Active only" }, { value: "false", label: "Inactive only" }]} />
            <button type="button" className="h-[40px] rounded-[12px] bg-slate-950 px-4 text-[12px] font-black text-white transition hover:bg-[#d86a78]" onClick={() => loadCoupons(1)}>Apply filters</button>
          </div>
        </div>

        <div className="mt-5 overflow-x-auto">
          <table className="min-w-full border-separate border-spacing-y-2 text-sm">
            <thead>
              <tr className="text-left text-[10px] font-black uppercase tracking-[0.16em] text-slate-400">
                <th className="px-3 py-2">Coupon</th><th className="px-3 py-2">Offer</th><th className="px-3 py-2">Scope</th><th className="px-3 py-2">Usage</th><th className="px-3 py-2">Validity</th><th className="px-3 py-2">Rules</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {normalizedItems.map((item) => (
                <React.Fragment key={item.id}>
                  <tr onClick={() => toggleUsage(item)} className={`group bg-[#fffafa] text-slate-700 shadow-[0_6px_16px_rgba(15,23,42,0.025)] transition ${!isWarehouseMode ? "cursor-pointer hover:-translate-y-0.5 hover:bg-[#fff4f5] hover:shadow-[0_12px_24px_rgba(216,106,120,0.08)]" : ""}`}>
                    <td className="rounded-l-[18px] border-y border-l border-rose-100 px-3 py-3.5">
                      <div className="flex items-start gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[14px] bg-rose-100 text-[#d86a78]"><TicketPercent size={17} /></div>
                        <div className="min-w-0"><div className="font-black tracking-wide text-slate-950">{item.code}</div><div className="max-w-[180px] truncate text-xs font-medium text-slate-500">{item.title || "Untitled offer"}</div>{String(item.createdByType || "").toUpperCase() === "ADMIN" ? <div className="mt-1 inline-flex rounded-full bg-white px-2 py-0.5 text-[10px] font-black text-[#cc4f60] shadow-sm">Admin coupon</div> : null}{item.rejectionReason ? <div className="mt-1 text-xs text-rose-600">{item.rejectionReason}</div> : null}</div>
                      </div>
                    </td>
                    <td className="border-y border-rose-100 px-3 py-3.5"><div className="text-[16px] font-black text-slate-950">{item.discountType === "PERCENT" ? `${item.discountValue}%` : money(item.discountValue)}</div>{item.discountType === "PERCENT" && item.maxDiscountAmount ? <div className="text-[11px] font-medium text-slate-500">Cap {money(item.maxDiscountAmount)}</div> : null}<div className="text-[11px] font-medium text-slate-500">Min {money(item.minOrderAmount)}</div></td>
                    <td className="border-y border-rose-100 px-3 py-3.5"><div className="max-w-[180px] font-bold text-slate-800">{warehouseLabel(item.warehouseId, warehouseMap)}</div></td>
                    <td className="border-y border-rose-100 px-3 py-3.5"><div className="font-black text-slate-900">{item.usedCount}/{item.usageLimit || "∞"}</div><div className="text-[11px] font-medium text-slate-500">Per user {item.perUserLimit}</div>{!isWarehouseMode ? <div className="mt-1 text-[10px] font-bold text-[#d86a78]">Click for details</div> : null}</td>
                    <td className="border-y border-rose-100 px-3 py-3.5"><div className="text-[11px] font-bold text-slate-700">{formatDate(item.startsAt)}</div><div className="mt-1 text-[10px] font-medium text-slate-500">to {formatDate(item.endsAt)}</div></td>
                    <td className="max-w-[280px] border-y border-rose-100 px-3 py-3.5 text-[11px] font-semibold text-slate-600">{formatRules(item)}</td>
                    <td className="border-y border-rose-100 px-3 py-3.5"><span className={`inline-flex rounded-full border px-2 py-1 text-[10px] font-black ${statusClass(item.approvalStatus)}`}>{item.approvalStatus}</span><div className={`mt-2 inline-flex rounded-full border px-2 py-1 text-[10px] font-black ${activeClass(item.isActive)}`}>{item.isActive ? "Active" : "Inactive"}</div></td>
                    <td className="rounded-r-[18px] border-y border-r border-rose-100 px-3 py-3.5"><div className="flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>{!isWarehouseMode && item.approvalStatus === "PENDING" ? <><button type="button" onClick={() => runAction(() => couponsService.approve(item.id), "Coupon approved.")} className="inline-flex items-center gap-1 rounded-lg bg-emerald-50 px-3 py-1.5 text-xs font-bold text-emerald-700"><CheckCircle2 size={14} /> Approve</button><button type="button" onClick={() => rejectCoupon(item.id)} className="inline-flex items-center gap-1 rounded-lg bg-rose-50 px-3 py-1.5 text-xs font-bold text-rose-700"><XCircle size={14} /> Reject</button></> : null}{isWarehouseMode && (item.readOnly || String(item.createdByType || "").toUpperCase() === "ADMIN") ? <span className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-bold text-slate-500">Read only</span> : <button type="button" onClick={() => runAction(() => api.setActive(item.id, !item.isActive), item.isActive ? "Coupon deactivated." : "Coupon activated.")} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-600 hover:border-[#d86a78] hover:text-[#d86a78]">{item.isActive ? "Deactivate" : "Activate"}</button>}{!isWarehouseMode ? <button type="button" onClick={() => deleteCoupon(item)} className="inline-flex items-center gap-1 rounded-lg border border-rose-200 bg-white px-3 py-1.5 text-xs font-bold text-rose-600"><Trash2 size={14} /> Delete</button> : null}</div></td>
                  </tr>
                  {renderUsagePanel(item)}
                </React.Fragment>
              ))}
              {!normalizedItems.length ? <tr><td colSpan="8" className="px-3 py-14 text-center"><div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-rose-50 text-[#d86a78]"><TicketPercent size={22} /></div><div className="mt-3 text-sm font-black text-slate-800">{loading ? "Loading coupons..." : "No coupons found"}</div><div className="mt-1 text-xs font-medium text-slate-400">Create a new offer or change your filters.</div></td></tr> : null}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );

}
